<template>
  <div>
    <Toolbar />
    <div class="container-xxl">
      <div class="card border-danger mb-5">
        <div class="card-body">
          <h2 class="text-danger mb-3">{{ pageTitle }}</h2>
          <p>{{ pageDescription }}</p>

          <div v-if="openCharges.length" class="mt-4">
            <h5 class="mb-3">Faturas em aberto</h5>
            <div class="table-responsive">
              <table class="table table-row-bordered align-middle">
                <thead>
                  <tr>
                    <th>Referência</th>
                    <th>Vencimento</th>
                    <th>Valor</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="charge in openCharges" :key="charge.id_place_platform_charge">
                    <td>{{ charge.charge_reference }}</td>
                    <td>{{ charge.charge_due_date }}</td>
                    <td>R$ {{ formatMoney(charge.charge_amount) }}</td>
                    <td>{{ charge.charge_status_label || charge.charge_status }}</td>
                    <td>
                      <a
                        v-if="charge.boleto_url"
                        :href="charge.boleto_url"
                        target="_blank"
                        class="btn btn-sm btn-light-primary me-1"
                      >
                        Boleto
                      </a>
                      <button
                        v-if="canManage && charge.charge_status !== 'paid'"
                        class="btn btn-sm btn-primary"
                        @click="openPayModal(charge)"
                      >
                        Pagar
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showCardForm && canManage" class="card mb-5">
        <div class="card-header">
          <h3 class="card-title">Atualizar cartão de crédito</h3>
        </div>
        <div class="card-body">
          <p class="text-muted mb-4">
            Cadastre um novo cartão para tentar cobrar as faturas pendentes automaticamente.
          </p>
          <PlatformSubscriptionCardForm
            ref="cardForm"
            :loading="savingCard"
            submit-label="Salvar cartão e tentar cobrança"
            @submit="saveCard"
          />
        </div>
      </div>

      <div class="d-flex gap-2 flex-wrap">
        <router-link v-if="canManage" to="/subscription/payment-method" class="btn btn-light-primary">
          Gerenciar forma de pagamento
        </router-link>
        <router-link to="/subscription/invoices" class="btn btn-light">Ver todas as faturas</router-link>
        <router-link to="/places" class="btn btn-light ms-auto">
          <i class="bi bi-arrow-left-right me-1"></i>
          Trocar de unidade
        </router-link>
      </div>
    </div>

    <PlatformSubscriptionPayChargeModal
      :charge="selectedCharge"
      :default-payment-method="subscription?.payment_method"
      :has-saved-card="hasSavedCard"
      @paid="onPaid"
      @close="selectedCharge = null"
    />
  </div>
</template>

<script>
import Toolbar from '../../components/layouts/auth/Toolbar.vue';
import PlatformSubscriptionCardForm from './PlatformSubscriptionCardForm.vue';
import PlatformSubscriptionPayChargeModal from './PlatformSubscriptionPayChargeModal.vue';
import http from '../../helpers/http';
import Alert from '../../helpers/alert';
import Permission from '../../helpers/permission';
import { isSubscriptionBlockedForBilling } from '../../helpers/platformSubscription';

export default {
  name: 'SubscriptionOverdue',
  components: { Toolbar, PlatformSubscriptionCardForm, PlatformSubscriptionPayChargeModal },
  data() {
    return {
      subscription: null,
      place: null,
      savingCard: false,
      selectedCharge: null,
    };
  },
  computed: {
    canManage() {
      return Permission.has('Manage Place Platform Subscription');
    },
    isSuspended() {
      return this.subscription?.status === 'suspended';
    },
    isPastDue() {
      return this.subscription?.status === 'past_due';
    },
    pageTitle() {
      if (this.isSuspended) {
        return 'Assinatura suspensa';
      }

      return 'Assinatura em atraso';
    },
    pageDescription() {
      if (this.isSuspended) {
        return 'Não foi possível cobrar o cartão cadastrado após várias tentativas. Atualize os dados do cartão para regularizar.';
      }

      return 'O acesso ao sistema está bloqueado até a regularização da mensalidade.';
    },
    openCharges() {
      return this.subscription?.open_charges || [];
    },
    showCardForm() {
      return this.isSuspended || this.subscription?.payment_method === 'credit_card';
    },
    hasSavedCard() {
      return !!this.subscription?.payment_method_details?.card;
    },
  },
  created() {
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      // A unidade em uso manda: trocar o local do usuário por conta própria
      // levava alguém com duas unidades para a errada.
      const current = this.$store.state.auth.place;
      if (current && isSubscriptionBlockedForBilling(current)) {
        this.place = current;
        await this.loadSubscription();
        return;
      }

      const user = this.$store.state.auth.user;
      const res = await http.get(`/users/${user.id_user}/places`);
      const blocked = res.data.find((p) => isSubscriptionBlockedForBilling(p));
      if (!blocked) {
        this.$router.push('/places');
        return;
      }

      this.place = blocked;
      await this.$store.dispatch('auth/selectPlace', blocked);
      await this.loadSubscription();
    },
    async loadSubscription() {
      const subRes = await http.get(`/places/${this.place.id_place}/platform-subscription`);
      this.subscription = subRes.data;
      this.$store.commit('auth/setPlatformSubscription', subRes.data);

      if (!subRes.data.is_blocked) {
        this.$router.push('/dashboard');
      }
    },
    formatMoney(value) {
      return Number(value).toFixed(2).replace('.', ',');
    },
    openPayModal(charge) {
      this.selectedCharge = { ...charge };
    },
    saveCard() {
      this.savingCard = true;
      const placeId = this.place.id_place;
      const payload = this.$refs.cardForm.payload();

      http.post(`/places/${placeId}/platform-subscription/payment-method/tokenize`, payload)
        .then(async (res) => {
          Alert.success('Cartão atualizado. Processando cobranças pendentes...');
          if (res.data.subscription) {
            this.subscription = res.data.subscription;
            this.$store.commit('auth/setPlatformSubscription', res.data.subscription);
          }
          await this.loadSubscription();
          if (!this.subscription?.is_blocked) {
            this.$router.push('/dashboard');
          }
        })
        .finally(() => {
          this.savingCard = false;
        });
    },
    onPaid() {
      this.loadSubscription();
    },
  },
};
</script>
